import React, { useState, useEffect, useRef } from 'react';
import log from 'loglevel';
import PropTypes from 'prop-types';

import { Flex } from 'Components/Base';
import Loading from 'Components/Loading';
import PdfViewer from 'Components/PdfViewer';
import { useClassroomContext } from 'Context/ClassroomContext';
import { useMyCourseData } from 'Domain/MyCourseData';
import { useWatermark } from 'Domain/PDFData';
import { withUserProfile } from 'Redux/user-profile/hoc';
import { useCapacitor } from 'Util/Capacitors';
import { useConfig } from 'Context/ConfigContext';

const CHECK_INVALID_DEVICE_INTERVAL = 1000;

const PdfBox = ({
  data,
  handleGetFileUrl,
  onCompleted,
  userProfile,
  assetReferId,
  checkInvalidDevice,
}) => {
  const { key, bucket, filename } = data;
  const downloadButton = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const { openUrl } = useCapacitor();
  const { getMarkedFileUrl, generatePayload } = useWatermark();
  const { courseCode } = useMyCourseData();
  const { currentSubsectionReferId } = useClassroomContext();
  const { classroom, uiVariant } = useConfig();
  const ebookConfig = classroom?.ebook ?? {};
  const { qrUrl = '', qrRef = 'additional6', enableQRRef = false } = ebookConfig;

  const handleLoadMarkedFile = async () => {
    const { url: fileUrl } = await getMarkedFileUrl(
      generatePayload({
        key,
        bucket,
        filename,
        userProfile,
        courseCode,
        ...(enableQRRef && {
          withQR: true,
          qrUrl: qrUrl
            .replace(':courseCode', courseCode)
            .replace(':subsectionReferId', currentSubsectionReferId),
          qrRef: userProfile[qrRef] ?? '',
        }),
      })
    );

    if (fileUrl) {
      downloadButton.current.href = fileUrl;
      if (typeof openUrl === 'function') {
        return openUrl(fileUrl, {
          asBrowser: true,
          defaultFunction: () => {
            downloadButton.current.click();
            downloadButton.current.href = '#';
          },
        });
      } else {
        downloadButton.current.click();
        downloadButton.current.href = '#';
        return;
      }
    }
  };

  const getAssetSrc = () => {
    if (key) {
      handleGetFileUrl(key, assetReferId).then((res) => {
        if (res) setPdfUrl(res);
      });
    }
    return () => setPdfUrl(() => null);
  };

  useEffect(getAssetSrc, []);

  useEffect(onCompleted, []);

  useEffect(() => {
    // TODO: Workaround to unset overflow Y for Skooldio layout
    if (uiVariant === 'PAAS') {
      const courseFixViewWrapper = document.getElementById('course-content-wrapper');
      courseFixViewWrapper.style = { overflowY: 'unset' };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkInvalidDeviceInterval = setInterval(() => {
      // this loop check invalid device (e.g. record screen) every `CHECK_INVALID_DEVICE_INTERVAL` ms
      // it should be check only when IDBook is preview.
      checkInvalidDevice();
    }, CHECK_INVALID_DEVICE_INTERVAL);
    return () => {
      clearInterval(checkInvalidDeviceInterval);
    };
  }, []);

  if (!pdfUrl) {
    return (
      <Flex width={1} height={'100%'} justifyContent={'center'} alignItems={'center'}>
        <Loading />
      </Flex>
    );
  }

  const handleError = (error) => {
    if (pdfUrl) setPdfUrl(() => null);
    log.warn(error);
  };

  return (
    <>
      <Flex
        flexDirection={'column'}
        width={'100%'}
        height={'100%'}
        alignItems="center"
        justifyContent="center"
      >
        <PdfViewer
          fileUrl={pdfUrl}
          onLoadPdf={handleLoadMarkedFile}
          pageProps={{ loading: null }}
          containerProps={{
            bg: 'black',
            flex: 1,
            minHeight: '100%',
            maxWidth: '1010px',
          }}
          documentContainerProps={{ pb: { _: '70vh', md: '50vh' } }}
          documentProps={{
            loading: (
              <Flex width={1} height={'100%'} justifyContent={'center'} alignItems={'center'}>
                <Loading />
              </Flex>
            ),
            onLoadSuccess: () => log.info('pdf asset loaded'),
            onLoadError: handleError,
            onSourceError: handleError,
            error: <h1>Coming soon</h1>,
          }}
        ></PdfViewer>
        <a
          href={'/#'}
          download
          style={{
            visibility: 'hidden',
            height: 0,
            width: 0,
            overflow: 'hidden',
            position: 'absolute',
          }}
          ref={downloadButton}
          rel="noopener noreferrer"
          target="_blank"
        >
          Download
        </a>
      </Flex>
    </>
  );
};

PdfBox.propTypes = {
  data: PropTypes.object,
  handleGetFileUrl: PropTypes.func,
  onCompleted: PropTypes.func,
  checkInvalidDevice: PropTypes.func,
};

PdfBox.defaultProps = {
  data: {},
  handleGetFileUrl: () => {},
  onCompleted: () => {},
  checkInvalidDevice: () => {},
};

export default withUserProfile(PdfBox);
